/**
 * EmailConfigPage — Phase 2 §2.2 (refactor post-incident bug fix)
 *
 * Fixes:
 *  - Correct endpoints: /admin/settings/email (GET / PUT / POST .../test / DELETE)
 *  - Field component DEFINED OUTSIDE the parent (was being remounted on every
 *    keystroke, which caused focus loss and truncated input like "s" / "485" /
 *    "@infos@remobiles.com").
 *  - Hostinger-friendly defaults (smtp.hostinger.com / 465 / SSL).
 *  - Sensible UX: password placeholder shows ******** when one is already set,
 *    only sent on PUT if the user actually retypes it.
 *  - Clear error/success states with FR text + red/black/gold theme.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  Mail, Save, Send, Server, Lock, User, AtSign, Hash, ShieldCheck, Trash2,
  Loader2, CheckCircle2, AlertCircle,
} from 'lucide-react';

// ─── Standalone Field (defined OUTSIDE EmailConfigPage to avoid remount) ────
function Field({
  icon: Icon, label, name, type = 'text', placeholder, required = false,
  value, onChange, autoComplete,
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider mb-1.5 font-medium text-gray-500">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
        <input
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? '' : parseInt(e.target.value, 10)) : e.target.value)}
          placeholder={placeholder}
          required={required}
          data-testid={`email-${name}`}
          autoComplete={autoComplete || 'off'}
          spellCheck={false}
          className="w-full pl-10 pr-4 py-3 bg-white/[0.04] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-amber-400/40 focus:ring-1 focus:ring-amber-400/20 transition-colors"
        />
      </div>
    </div>
  );
}

const HOSTINGER_DEFAULTS = {
  smtp_host: 'smtp.hostinger.com',
  smtp_port: 465,
  smtp_user: '',
  smtp_password: '',
  sender_name: 'Contact R-E-Mobiles',
  sender_email: '',
  use_ssl: true,
  enabled: true,
};

const EmailConfigPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [config, setConfig] = useState(HOSTINGER_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  // Track whether the user has typed a NEW password since the form loaded.
  // If false on save, we send `null` so the backend keeps the existing one.
  const [pwdDirty, setPwdDirty] = useState(false);
  const [hasExistingPwd, setHasExistingPwd] = useState(false);

  // ── Load on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    api.get('/admin/settings/email')
      .then((r) => {
        if (!alive) return;
        const d = r.data || {};
        if (d.configured) {
          setConfig({
            smtp_host:    d.smtp_host    || HOSTINGER_DEFAULTS.smtp_host,
            smtp_port:    d.smtp_port    || HOSTINGER_DEFAULTS.smtp_port,
            smtp_user:    d.smtp_user    || '',
            smtp_password: '', // never expose hash; user re-types if needed
            sender_name:  d.sender_name  || HOSTINGER_DEFAULTS.sender_name,
            sender_email: d.sender_email || '',
            use_ssl:      d.use_ssl ?? true,
            enabled:      d.enabled ?? true,
          });
          setHasExistingPwd(d.smtp_password === '********');
        }
      })
      .catch((err) => {
        // 404 (route absent) or 500 — show actionable message
        const status = err.response?.status;
        if (status && status !== 404) {
          setMsg({ type: 'error', text: t('emailConfig.loadError', { status }) });
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const setField = useCallback((name, value) => {
    setConfig((prev) => ({ ...prev, [name]: value }));
    if (name === 'smtp_password') setPwdDirty(true);
  }, []);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const payload = { ...config };
      // Only send password when user actually typed something new
      if (!pwdDirty || !payload.smtp_password) delete payload.smtp_password;
      await api.put('/admin/settings/email', payload);
      setMsg({ type: 'success', text: t('emailConfig.saveSuccess') });
      setHasExistingPwd(true);
      setPwdDirty(false);
      setConfig((c) => ({ ...c, smtp_password: '' }));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || t('emailConfig.saveErrorFallback');
      setMsg({ type: 'error', text: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally { setSaving(false); }
  };

  // ── Test SMTP ────────────────────────────────────────────────────────────
  const handleTest = async () => {
    setTesting(true); setMsg(null);
    try {
      const r = await api.post('/admin/settings/email/test');
      setMsg({ type: 'success', text: r.data?.message || t('emailConfig.testSuccess') });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msgTxt = typeof detail === 'string' ? detail : t('emailConfig.testErrorFallback');
      setMsg({ type: 'error', text: msgTxt });
    } finally { setTesting(false); }
  };

  // ── Disable ──────────────────────────────────────────────────────────────
  const handleDisable = async () => {
    if (!window.confirm(t('emailConfig.disableConfirm'))) return;
    try {
      await api.delete('/admin/settings/email');
      setConfig(HOSTINGER_DEFAULTS);
      setHasExistingPwd(false);
      setPwdDirty(false);
      setMsg({ type: 'success', text: t('emailConfig.disableSuccess') });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || t('emailConfig.disableErrorFallback') });
    }
  };

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="email-config-page">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-amber-400" /> {t('emailConfig.pageTitle')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('emailConfig.pageSubtitle')}
          </p>
        </div>

        {msg && (
          <div
            data-testid="email-config-msg"
            className={`px-4 py-3 rounded-lg text-sm font-medium flex items-start gap-2 ${
              msg.type === 'success'
                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
                : 'bg-red-500/10 border border-red-500/30 text-red-300'
            }`}
          >
            {msg.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <div>{msg.text}</div>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4" autoComplete="off">
          {/* SMTP server section */}
          <div className="bg-[#0f0f0f] border border-white/8 rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Server className="w-4 h-4 text-amber-400" /> {t('emailConfig.smtpSection')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field icon={Server} label={t('emailConfig.fieldHost')} name="smtp_host" placeholder="smtp.hostinger.com" required
                value={config.smtp_host} onChange={(v) => setField('smtp_host', v)} />
              <Field icon={Hash} label={t('emailConfig.fieldPort')} name="smtp_port" type="number" placeholder="465"
                value={config.smtp_port} onChange={(v) => setField('smtp_port', v)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field icon={User} label={t('emailConfig.fieldUser')} name="smtp_user"
                placeholder="contact@remobiles.com" required autoComplete="username"
                value={config.smtp_user} onChange={(v) => setField('smtp_user', v)} />
              <Field icon={Lock} label={t('emailConfig.fieldPassword')} name="smtp_password" type="password"
                placeholder={hasExistingPwd ? t('emailConfig.passwordPlaceholderExisting') : t('emailConfig.passwordPlaceholderNew')}
                autoComplete="new-password"
                value={config.smtp_password} onChange={(v) => setField('smtp_password', v)} />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.use_ssl}
                  onChange={(e) => setField('use_ssl', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 text-amber-400 focus:ring-amber-400/20"
                  data-testid="email-use_ssl"
                />
                <span className="text-xs text-gray-400">
                  <ShieldCheck className="w-3 h-3 inline mr-1" />{t('emailConfig.useSsl')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer ml-auto">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setField('enabled', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 text-amber-400 focus:ring-amber-400/20"
                  data-testid="email-enabled"
                />
                <span className="text-xs text-gray-400">{t('emailConfig.enableEmails')}</span>
              </label>
            </div>
            <div className="text-[11px] text-amber-400/70 bg-amber-400/5 border border-amber-400/20 rounded-lg p-3">
              <strong>{t('emailConfig.tipTitle')}</strong> {t('emailConfig.tipBody')}
            </div>
          </div>

          {/* Sender section */}
          <div className="bg-[#0f0f0f] border border-white/8 rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <AtSign className="w-4 h-4 text-amber-400" /> {t('emailConfig.senderSection')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field icon={User} label={t('emailConfig.fieldSenderName')} name="sender_name" placeholder="Contact R-E-Mobiles"
                value={config.sender_name} onChange={(v) => setField('sender_name', v)} />
              <Field icon={AtSign} label={t('emailConfig.fieldSenderEmail')} name="sender_email" type="email"
                placeholder="infos@remobiles.com" required
                value={config.sender_email} onChange={(v) => setField('sender_email', v)} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={saving} data-testid="email-save-btn"
              className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
              style={{
                background: 'linear-gradient(145deg, #d11833 0%, #8a0a1d 100%)',
                color: '#fff',
                border: '1px solid #d4af37',
                boxShadow: '0 6px 18px rgba(200,16,46,0.3)',
              }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t('emailConfig.saving') : t('emailConfig.save')}
            </button>
            <button type="button" onClick={handleTest} disabled={testing || !config.smtp_host} data-testid="email-test-btn"
              className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-amber-400/10 text-amber-400 border border-amber-400/30 transition-all disabled:opacity-50 flex items-center gap-2 hover:bg-amber-400/15">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {testing ? t('emailConfig.testing') : t('emailConfig.testConnection')}
            </button>
            <button type="button" onClick={handleDisable} data-testid="email-disable-btn"
              className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-500/10 text-red-400 border border-red-500/30 transition-all flex items-center gap-2 hover:bg-red-500/15 ml-auto">
              <Trash2 className="w-4 h-4" /> {t('emailConfig.disable')}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EmailConfigPage;
